import type {
  SupabaseClient,
  Tables,
  UnitType,
} from "@repo/supabase/database-types"
import { logger } from "@repo/utils/logger"
import type { UnitRolePermissionsMap } from "./types"

export type Role = Tables<"roles">

export const createRolesAndPermissionsService = (supabase: SupabaseClient) => {
  return new RolesAndPermissionsService(supabase)
}

class RolesAndPermissionsService {
  log = logger.child({
    module: "roles-and-permissions/service",
  })

  constructor(private readonly supabase: SupabaseClient) {}

  async getAppRole() {
    const { data, error } = await this.supabase.rpc("get_app_role")

    if (error) {
      this.log.error("Error fetching app role", { error })
      throw new Error("Failed to fetch app role", { cause: error })
    }

    return data
  }

  async isSuperAdmin() {
    const { data, error } = await this.supabase.rpc("is_super_admin")

    if (error) {
      this.log.error("Error checking if user is super admin", { error })
      throw new Error("Failed to check admin status", { cause: error })
    }

    return data
  }

  async isInternalUser() {
    const { data, error } = await this.supabase.rpc("is_internal_user")

    if (error) {
      this.log.error("Error checking if user is internal user", { error })
      throw new Error("Failed to check internal user status", { cause: error })
    }

    return data
  }

  async getRoleId({
    roleName,
    unitType,
  }: {
    roleName: string
    unitType: UnitType
  }) {
    const { data, error } = await this.supabase.rpc("get_role_id", {
      p_role_name: roleName,
      p_unit_type: unitType,
    })

    if (error) {
      this.log.error("Error fetching role id", { error, roleName, unitType })
      throw new Error("Failed to fetch role", { cause: error })
    }

    return data
  }

  async getCurrentUnitRole({ unitId }: { unitId: string }) {
    const { data, error } = await this.supabase.rpc("current_user_unit_role", {
      p_unit_id: unitId,
    })

    if (error) {
      this.log.error("Error fetching current user's unit role", {
        error,
        unitId,
      })
      throw new Error("Failed to fetch unit role", { cause: error })
    }

    return data
  }

  async getCurrentUserRolePermissions() {
    const { data, error } = await this.supabase.rpc(
      "get_current_user_unit_permissions",
    )

    if (error) {
      this.log.error("Error fetching current user's role permissions", {
        error,
      })
      throw new Error("Failed to fetch permissions", { cause: error })
    }

    if (!data) {
      this.log.error("No data returned from current user's role permissions")
      throw new Error("Failed to fetch permissions")
    }

    // Transform the compact database format to the expected map format
    return data.reduce((acc, curr) => {
      if (!curr.unit_id) return acc

      acc[curr.unit_id] = {
        unitId: curr.unit_id,
        unitType: curr.unit_type,
        unitSlug: curr.unit_slug,
        roleId: curr.role_id,
        roleName: curr.role_name,
        level: curr.role_level,
        editable: curr.role_editable,
        permissions: curr.permissions || [],
        isInherited: curr.is_inherited,
        inheritedFromUnitId: curr.inherited_from_unit_id,
        inheritedFromUnitSlug: curr.inherited_from_unit_slug,
      }

      return acc
    }, {} as UnitRolePermissionsMap)
  }

  async getMostPrivilegedRole({
    unitType,
  }: {
    unitType: UnitType
  }): Promise<Role> {
    const { data, error } = await this.supabase.rpc(
      "get_most_privileged_role",
      { p_unit_type: unitType },
    )

    if (error) {
      this.log.error("Error fetching most privileged role", { error, unitType })
      throw new Error("Failed to fetch role", { cause: error })
    }

    return data
  }

  async getLeastPrivilegedRole({
    unitType,
  }: {
    unitType: UnitType
  }): Promise<Role> {
    const { data, error } = await this.supabase.rpc(
      "get_least_privileged_role",
      { p_unit_type: unitType },
    )

    if (error) {
      this.log.error("Error fetching least privileged role", {
        error,
        unitType,
      })
      throw new Error("Failed to fetch role", { cause: error })
    }

    return data
  }

  async hasRoleOnUnit({ unitId, roleId }: { unitId: string; roleId: number }) {
    const { data, error } = await this.supabase.rpc("has_role_on_unit", {
      p_unit_id: unitId,
      p_role_id: roleId,
    })

    if (error) {
      this.log.error("Error checking if has role on unit", {
        error,
        unitId,
        roleId,
      })
      throw new Error("Failed to check role", { cause: error })
    }

    return data
  }

  async hasSameLevelRole({
    unitId,
    roleId,
    userId,
  }: {
    unitId: string
    roleId: number
    userId: string
  }) {
    const { data, error } = await this.supabase.rpc("has_same_level_role", {
      p_target_unit_id: unitId,
      p_target_role_id: roleId,
      p_target_user_id: userId,
    })

    if (error) {
      this.log.error("Error checking if has same level role", {
        error,
        unitId,
        roleId,
        userId,
      })
      throw new Error("Failed to check role", { cause: error })
    }

    return data
  }

  async hasMoreElevatedRole({
    unitId,
    roleId,
    userId,
  }: {
    unitId: string
    roleId: number
    userId: string
  }) {
    const { data, error } = await this.supabase.rpc("has_more_elevated_role", {
      p_target_unit_id: unitId,
      p_target_role_id: roleId,
      p_target_user_id: userId,
    })

    if (error) {
      this.log.error("Error checking if has more elevated role", {
        error,
        unitId,
        roleId,
        userId,
      })
      throw new Error("Failed to check role", { cause: error })
    }

    return data
  }

  async isPrimaryUnitOwner({ unitId }: { unitId: string }) {
    const { data, error } = await this.supabase.rpc("is_primary_unit_owner", {
      p_unit_id: unitId,
    })

    if (error) {
      this.log.error("Error checking if user is primary owner of unit", {
        error,
        unitId,
      })
      throw new Error("Failed to check ownership", { cause: error })
    }

    return data
  }

  async canAssignRole({ unitId, roleId }: { unitId: string; roleId: number }) {
    const { data, error } = await this.supabase.rpc("can_assign_role", {
      p_unit_id: unitId,
      p_role_to_assign_id: roleId,
    })

    if (error) {
      this.log.error("Error checking if can assign role", {
        error,
        unitId,
        roleId,
      })
      throw new Error("Failed to check permission", { cause: error })
    }

    return data
  }

  async canActionUnitUser({
    unitId,
    userId,
    permissionNeeded,
  }: {
    unitId: string
    userId: string
    permissionNeeded: string
  }) {
    const { data, error } = await this.supabase.rpc("can_action_unit_user", {
      p_target_unit_id: unitId,
      p_target_user_id: userId,
      p_permission_needed: permissionNeeded,
    })

    if (error) {
      this.log.error("Error checking if can action unit user", {
        error,
        unitId,
        userId,
        permissionNeeded,
      })
      throw new Error("Failed to check permission", { cause: error })
    }

    return data
  }

  async updateUnitUserRole({
    unitId,
    userId,
    roleId,
    makePrimaryOwner,
  }: {
    unitId: string
    userId: string
    roleId: number
    makePrimaryOwner?: boolean
  }) {
    const { data, error } = await this.supabase.rpc("update_unit_user_role", {
      p_unit_id: unitId,
      p_user_id: userId,
      p_role_id: roleId,
      p_make_primary_owner: makePrimaryOwner,
    })

    if (error) {
      this.log.error("Error updating unit user role", {
        error,
        unitId,
        userId,
        roleId,
        makePrimaryOwner,
      })
      throw new Error("Failed to update role", { cause: error })
    }

    return data
  }

  async getAssignableRolesForUnit({
    unitId,
  }: {
    unitId: string
  }): Promise<Role[]> {
    const { data, error } = await this.supabase.rpc(
      "get_assignable_roles_for_unit",
      {
        p_unit_id: unitId,
      },
    )

    if (error) {
      this.log.error("Error fetching assignable roles for unit", {
        error,
        unitId,
      })
      throw new Error("Failed to fetch assignable roles", { cause: error })
    }

    return data
  }
}
